import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ECONode, Organization, OrgHat, OrgPart } from '@newgenus/common';
import { Position } from '../../services/org-board-builder.service';
import { OrgHattingService } from '../../services/org-hatting.service';
import { OrgBoardService } from '../../services/org-board-service';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { linkOrigin, linkSummary } from '../../linked-posts';

interface AssignableUser {
    key: string;
    name: string;
    email?: string;
    photoThumbnail?: string;
    profileColor?: string;
    isPending?: boolean;
    approvalId?: string;
    /** Phase B.3 — true when this user has an outstanding invitation
     *  (S1 Pending Acceptance). Approve action must be gated while true. */
    awaitingAcceptance?: boolean;
    selectedHatKeys?: string[];
    hatNames?: string[];
    /** Hat keys the user currently wears on this row — both position hats on
     *  this post and any org-wide special-rights hats they carry. Used by the
     *  "Add hats to existing user" flow to filter the picker. */
    hatKeys?: string[];
    /** Hat keys queued to be added to this already-assigned user (modify flow). */
    hatKeysToAdd?: string[];
    /** UI flag — inline hat picker expanded on this user row. */
    showHatPicker?: boolean;
}

interface TreeRow {
    partKey: string;
    parentRowKey: string;
    name: string;
    partType: string;
    indent: number;
    isPost: boolean;
    isCollapsed: boolean;
    hasChildren: boolean;
    currentUsers: AssignableUser[];
    hats: OrgHat[];
    specialRightsHats: OrgHat[];
    usersToAdd: AssignableUser[];
    usersToRemove: AssignableUser[];
    usersToApprove: AssignableUser[];
    usersToReject: AssignableUser[];
    /** Existing users (already on the post) who are being given additional
     *  hats — either unworn position hats on this post, or special-rights
     *  hats they don't already carry. */
    usersToModify: AssignableUser[];
    searchText: string;
    filteredUsers: AssignableUser[];
    noHats: boolean;
    /** Set when this post REPRESENTS another post (linked post): nobody is assigned here — the
     *  row says who it shows and points at the origin instead of offering "Search & add user". */
    linkedTo?: { partKey: string; name: string; summary: string };
}

export interface BulkAssignStaffDialogData {
    node: ECONode<Position>;
    organization: Organization;
}

@Component({
    selector: 'shared-org-bulk-assign-staff-dialog',
    imports: [CommonModule, MaterialModule, FormsModule],
    templateUrl: './org-bulk-assign-staff-dialog.component.html',
    styleUrls: ['./org-bulk-assign-staff-dialog.component.scss', '../org-dialog.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class OrgBulkAssignStaffDialogComponent implements OnInit, OnDestroy {
    public rows: TreeRow[] = [];
    public allUsers: AssignableUser[] = [];
    public isSaving = false;
    public saveProgress = 0;
    public totalChanges = 0;
    public requireApproval = false;
    public treeFilterTerm = '';
    public matchedPartKeys: Set<string> = new Set();

    // ── Two-phase save state ──
    savePhase: 'idle' | 'writing' | 'processing' | 'done' | 'error' = 'idle';
    taskStatuses: { key: string; name: string; action: string; icon: string; status: string; statusIcon: string }[] =
        [];
    saveResults: {
        userName: string;
        postName: string;
        outcome: 'posted' | 'awaiting approval' | 'approved' | 'rejected' | 'removed' | 'failed';
        icon: string;
        iconClass: string;
    }[] = [];
    tasksCompleted = 0;
    totalTasks = 0;
    private saveStartTime = 0;
    private taskMonitorSub?: Subscription;

    constructor(
        public dialogRef: MatDialogRef<OrgBulkAssignStaffDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: BulkAssignStaffDialogData,
        private orgHattingService: OrgHattingService,
        private snackBar: MatSnackBar,
        private obService: OrgBoardService,
        public colorsService: OrgEntityColorsService
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets) — the class is phone-scoped globally, so the
        // desktop 65% window is unchanged; the layout stacks in this component's phone block.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    ngOnDestroy(): void {
        this.taskMonitorSub?.unsubscribe();
    }

    async ngOnInit(): Promise<void> {
        this.requireApproval = !!this.data.organization.settings?.requireHatAssignmentApproval;
        this.buildUserList();
        this.buildTree();
        await this.loadPendingApprovals();
        await this.loadPendingInvitationGate();
    }

    private buildUserList(): void {
        const org = this.data.organization;
        if (!org?.orgUsers?.userKeys) return;

        this.allUsers = org.orgUsers.userKeys
            .map((key) => {
                const user = org.orgUsers[key];
                return {
                    key,
                    name: user?.name || user?.userName || key,
                    email: user?.email,
                    photoThumbnail: user?.photoThumbnail,
                    profileColor: user?.profileColor,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    private buildTree(): void {
        const org = this.data.organization;
        const rootKey = this.data.node?.data?.partKey;
        if (!org?.orgParts || !rootKey) return;

        // When invoked from a single post's context menu, the "root" itself is
        // the post we want to assign staff to. flattenChildren only emits
        // descendants, so prepend a row for the post in that case. For
        // section/unit/department roots we keep the original behaviour
        // (descendants only) because those container rows aren't assignable.
        const rootPart = org.orgParts.find((p) => p.partKey === rootKey);
        const rootIsPost = rootPart?.partType === 'post';

        this.rows =
            rootIsPost && rootPart
                ? [this.buildRowForPart(rootPart, org.orgParts, 0), ...this.flattenChildren(rootKey, org.orgParts, 1)]
                : this.flattenChildren(rootKey, org.orgParts, 0);
    }

    /**
     * Build a single TreeRow for the given part. Extracted from flattenChildren
     * so the single-post entry path can reuse the same row-construction logic.
     */
    private buildRowForPart(part: OrgPart, orgParts: OrgPart[], indent: number): TreeRow {
        const isPost = part.partType === 'post';
        const hats = this.orgHattingService.getHatsForPosition(this.data.organization, part.partKey);
        const currentUsers = isPost ? this.resolveCurrentUsers(hats) : [];
        const childParts = isPost ? [] : orgParts.filter((p) => p.parentPartKey === part.partKey);
        const positionHatKeys = new Set(hats.map((h) => h.key));
        const specialRightsHats = isPost
            ? this.orgHattingService
                  .getSpecialRightsHats(this.data.organization)
                  .filter((h) => !positionHatKeys.has(h.key))
            : [];

        return {
            partKey: part.partKey,
            parentRowKey: part.parentPartKey || '',
            name: part.name,
            partType: part.partType || 'post',
            indent,
            isPost,
            isCollapsed: false,
            hasChildren: childParts.length > 0,
            currentUsers,
            hats,
            specialRightsHats,
            usersToAdd: [],
            usersToRemove: [],
            usersToApprove: [],
            usersToReject: [],
            usersToModify: [],
            searchText: '',
            filteredUsers: [],
            noHats: isPost && hats.length === 0,
            linkedTo: isPost ? this.resolveLink(part.partKey) : undefined,
        };
    }

    /** A linked post's origin + who it currently shows (the origin's active holders). */
    private resolveLink(partKey: string): TreeRow['linkedTo'] {
        const origin = linkOrigin(this.data.organization?.orgParts as any[], partKey);
        if (!origin) return undefined;
        const targetHats = this.orgHattingService.getHatsForPosition(this.data.organization, origin.partKey);
        const holders = this.resolveCurrentUsers(targetHats)
            .filter((u) => !u.isPending)
            .map((u) => u.name);
        return { partKey: origin.partKey, name: origin.name, summary: linkSummary(holders) };
    }

    /** "Go to origin" on a linked post: close and let the board open the represented post. */
    public goToOrigin(row: TreeRow): void {
        if (!row.linkedTo) return;
        this.dialogRef.close();
        this.obService.navigateToPart.next(row.linkedTo.partKey);
    }

    private flattenChildren(parentKey: string, orgParts: OrgPart[], indent: number): TreeRow[] {
        const rows: TreeRow[] = [];
        const directChildren = orgParts
            .filter((p) => p.parentPartKey === parentKey)
            .sort((a, b) => this.extractPathIndex(a.path) - this.extractPathIndex(b.path));

        for (const child of directChildren) {
            rows.push(this.buildRowForPart(child, orgParts, indent));

            if (child.partType !== 'post') {
                rows.push(...this.flattenChildren(child.partKey, orgParts, indent + 1));
            }
        }

        return rows;
    }

    private resolveCurrentUsers(hats: OrgHat[]): AssignableUser[] {
        const userMap = new Map<string, AssignableUser>();
        const org = this.data.organization;

        // Active users (assignedToArray)
        for (const hat of hats) {
            if (hat.assignedToArray) {
                for (const uid of hat.assignedToArray) {
                    if (!userMap.has(uid)) {
                        const orgUser = org.orgUsers?.[uid];
                        userMap.set(uid, {
                            key: uid,
                            name: orgUser?.name || orgUser?.userName || uid,
                            email: orgUser?.email,
                            photoThumbnail: orgUser?.photoThumbnail,
                            profileColor: orgUser?.profileColor,
                            isPending: false,
                            hatNames: [hat.name],
                            hatKeys: [hat.key],
                        });
                    } else {
                        const existing = userMap.get(uid)!;
                        existing.hatNames!.push(hat.name);
                        existing.hatKeys!.push(hat.key);
                    }
                }
            }

            // Pending users (pendingAssignedToArray)
            if (hat.pendingAssignedToArray) {
                for (const uid of hat.pendingAssignedToArray) {
                    if (!userMap.has(uid)) {
                        const orgUser = org.orgUsers?.[uid];
                        userMap.set(uid, {
                            key: uid,
                            name: orgUser?.name || orgUser?.userName || uid,
                            email: orgUser?.email,
                            photoThumbnail: orgUser?.photoThumbnail,
                            profileColor: orgUser?.profileColor,
                            isPending: true,
                            hatNames: [hat.name],
                            hatKeys: [hat.key],
                        });
                    } else {
                        const existing = userMap.get(uid)!;
                        existing.hatNames!.push(hat.name);
                        existing.hatKeys!.push(hat.key);
                    }
                }
            }
        }

        // Merge in any org-wide special-rights hats these users already wear —
        // so the modify picker correctly excludes what they already carry.
        // (We don't create new entries here; only enrich users already resolved
        //  via a position hat on this post.)
        const specialRightsHats = this.orgHattingService.getSpecialRightsHats(org);
        for (const hat of specialRightsHats) {
            const both = [...(hat.assignedToArray || []), ...(hat.pendingAssignedToArray || [])];
            for (const uid of both) {
                const existing = userMap.get(uid);
                if (!existing) continue;
                if (!existing.hatKeys!.includes(hat.key)) {
                    existing.hatKeys!.push(hat.key);
                    existing.hatNames!.push(hat.name);
                }
            }
        }

        // Sort: active first, then pending, alphabetical within each
        return Array.from(userMap.values()).sort((a, b) => {
            if (a.isPending !== b.isPending) return a.isPending ? 1 : -1;
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * Load pending approval records and attach approvalIds to pending users in tree rows.
     */
    private async loadPendingApprovals(): Promise<void> {
        const orgId = this.data.organization.key;
        try {
            const approvals = await this.orgHattingService.getPendingApprovals(orgId, 'pending');
            // Map approvalId onto the matching pending user in each row
            for (const approval of approvals) {
                for (const row of this.rows) {
                    if (!row.isPost) continue;
                    if (approval.positionKey !== row.partKey) continue;
                    const pendingUser = row.currentUsers.find((u) => u.key === approval.uid && u.isPending);
                    if (pendingUser) {
                        pendingUser.approvalId = approval.key;
                    }
                }
            }
        } catch (error) {
            console.error('[BulkAssignStaff] Error loading pending approvals:', error);
        }
    }

    /**
     * Phase B.3 \u2014 tag each pending user across all rows with `awaitingAcceptance`
     * when they still have an outstanding invitation for this org. Templates use
     * the flag to render a dashed "Pending Acceptance" pill and to disable the
     * Approve action (the backend also gates this in `approvePendingUserDirectly`).
     */
    private async loadPendingInvitationGate(): Promise<void> {
        const orgId = this.data.organization.key;
        try {
            const uids = await this.orgHattingService.getPendingInvitationUidsForOrg(orgId);
            for (const row of this.rows) {
                for (const user of row.currentUsers) {
                    if (user.isPending) {
                        user.awaitingAcceptance = uids.has(user.key);
                    }
                }
            }
        } catch (error) {
            console.error('[BulkAssignStaff] Error loading pending invitation gate:', error);
        }
    }

    public onApproveUser(row: TreeRow, user: AssignableUser): void {
        if (!user.isPending) return;
        // Phase B.3 \u2014 gate: refuse to queue approval if user hasn't accepted yet.
        if (user.awaitingAcceptance) {
            this.snackBar.open(
                `${user.name} hasn't accepted their invitation yet. Approval becomes available after they accept.`,
                'OK',
                { duration: 4000 }
            );
            return;
        }
        // If already queued for rejection, undo that first
        const rejectIdx = row.usersToReject.findIndex((u) => u.key === user.key);
        if (rejectIdx >= 0) row.usersToReject.splice(rejectIdx, 1);
        // Queue for approval if not already queued
        if (!row.usersToApprove.some((u) => u.key === user.key)) {
            row.usersToApprove.push(user);
        }
    }

    public onRejectUser(row: TreeRow, user: AssignableUser): void {
        if (!user.isPending) return;
        // If already queued for approval, undo that first
        const approveIdx = row.usersToApprove.findIndex((u) => u.key === user.key);
        if (approveIdx >= 0) row.usersToApprove.splice(approveIdx, 1);
        // Queue for rejection if not already queued
        if (!row.usersToReject.some((u) => u.key === user.key)) {
            row.usersToReject.push(user);
        }
    }

    public onUndoApproveUser(row: TreeRow, user: AssignableUser): void {
        const idx = row.usersToApprove.findIndex((u) => u.key === user.key);
        if (idx >= 0) row.usersToApprove.splice(idx, 1);
    }

    public onUndoRejectUser(row: TreeRow, user: AssignableUser): void {
        const idx = row.usersToReject.findIndex((u) => u.key === user.key);
        if (idx >= 0) row.usersToReject.splice(idx, 1);
    }

    public isUserBeingApproved(row: TreeRow, user: AssignableUser): boolean {
        return row.usersToApprove.some((u) => u.key === user.key);
    }

    public isUserBeingRejected(row: TreeRow, user: AssignableUser): boolean {
        return row.usersToReject.some((u) => u.key === user.key);
    }

    private extractPathIndex(path: string): number {
        if (!path) return 0;
        const parts = path.split('/');
        const lastPart = parts[parts.length - 1];
        const num = parseInt(lastPart, 10);
        return isNaN(num) ? 0 : num;
    }

    public toggleCollapse(row: TreeRow): void {
        row.isCollapsed = !row.isCollapsed;
    }

    public onTreeFilterChange(): void {
        const term = this.treeFilterTerm.trim().toLowerCase();
        this.matchedPartKeys.clear();

        if (!term) return;

        // Find rows whose name matches the search
        const directMatches: TreeRow[] = [];
        for (const row of this.rows) {
            if (fuzzyMatch(row.name.toLowerCase(), term)) {
                this.matchedPartKeys.add(row.partKey);
                directMatches.push(row);
                // Also include all ancestors so the path is visible
                this.addAncestors(row.parentRowKey);
            }
        }

        // For matched non-post rows, also include all descendants
        for (const match of directMatches) {
            if (!match.isPost) {
                this.addDescendants(match.partKey);
            }
        }

        // Auto-expand matched ancestor headers
        for (const row of this.rows) {
            if (!row.isPost && this.matchedPartKeys.has(row.partKey)) {
                row.isCollapsed = false;
            }
        }
    }

    private addDescendants(parentKey: string): void {
        for (const row of this.rows) {
            if (row.parentRowKey === parentKey) {
                this.matchedPartKeys.add(row.partKey);
                if (!row.isPost) {
                    this.addDescendants(row.partKey);
                }
            }
        }
    }

    private addAncestors(parentKey: string): void {
        let key = parentKey;
        while (key) {
            const parentRow = this.rows.find((r) => r.partKey === key);
            if (!parentRow || this.matchedPartKeys.has(key)) break;
            this.matchedPartKeys.add(key);
            key = parentRow.parentRowKey;
        }
    }

    public clearTreeFilter(): void {
        this.treeFilterTerm = '';
        this.matchedPartKeys.clear();
    }

    public isRowVisible(row: TreeRow): boolean {
        // If filtering, only show matched rows
        if (this.treeFilterTerm.trim() && this.matchedPartKeys.size > 0) {
            if (!this.matchedPartKeys.has(row.partKey)) return false;
        } else if (this.treeFilterTerm.trim() && this.matchedPartKeys.size === 0) {
            // Search active but nothing matched
            return false;
        }

        // Walk up the parent chain; if any ancestor is collapsed, this row is hidden
        let currentParentKey = row.parentRowKey;
        while (currentParentKey) {
            const parentRow = this.rows.find((r) => r.partKey === currentParentKey);
            if (!parentRow) break;
            if (parentRow.isCollapsed) return false;
            currentParentKey = parentRow.parentRowKey;
        }
        return true;
    }

    public onSearchChange(row: TreeRow): void {
        this.filterAvailableUsers(row);
    }

    public onSearchFocus(row: TreeRow): void {
        this.filterAvailableUsers(row);
    }

    private filterAvailableUsers(row: TreeRow): void {
        const term = (row.searchText || '').toLowerCase().trim();

        const assignedKeys = new Set<string>();
        for (const u of row.currentUsers) assignedKeys.add(u.key);
        for (const u of row.usersToAdd) assignedKeys.add(u.key);
        for (const u of row.usersToRemove) assignedKeys.delete(u.key);

        row.filteredUsers = this.allUsers.filter((user) => {
            if (assignedKeys.has(user.key)) return false;
            if (!term) return true;
            return user.name.toLowerCase().includes(term) || (user.email && user.email.toLowerCase().includes(term));
        });
    }

    public onUserSelected(row: TreeRow, event: MatAutocompleteSelectedEvent, inputEl?: HTMLInputElement): void {
        const userKey = event.option.value;
        const user = this.allUsers.find((u) => u.key === userKey);
        if (!user) return;

        const removeIdx = row.usersToRemove.findIndex((u) => u.key === userKey);
        if (removeIdx >= 0) {
            row.usersToRemove.splice(removeIdx, 1);
        } else {
            // Auto-select all position hats by default
            const newUser = { ...user, selectedHatKeys: row.hats.map((h) => h.key) };
            row.usersToAdd.push(newUser);
        }

        row.searchText = '';
        row.filteredUsers = [];
        // Selection COMPLETES the interaction. The input keeps focus after a
        // pick and sits right under the pointer (the option list renders over
        // it), so a stray click immediately re-opened the full picker —
        // "sometimes a second Add User opens by itself" (ISS-007). Blur makes
        // it deterministic: adding another user is always an intentional
        // click back into the field.
        inputEl?.blur();
    }

    /** Display function for mat-autocomplete — shows user name instead of key */
    public displayUserName = (value: string): string => {
        if (!value) return '';
        const user = this.allUsers.find((u) => u.key === value);
        return user?.name || '';
    };

    public onRemoveCurrentUser(row: TreeRow, user: AssignableUser): void {
        row.usersToRemove.push(user);
    }

    public onUndoRemoveUser(row: TreeRow, user: AssignableUser): void {
        const idx = row.usersToRemove.findIndex((u) => u.key === user.key);
        if (idx >= 0) row.usersToRemove.splice(idx, 1);
    }

    public onRemoveNewUser(row: TreeRow, user: AssignableUser): void {
        const idx = row.usersToAdd.findIndex((u) => u.key === user.key);
        if (idx >= 0) row.usersToAdd.splice(idx, 1);
    }

    public onHatSelectionChange(row: TreeRow, user: AssignableUser, selectedKeys: string[]): void {
        user.selectedHatKeys = selectedKeys;
    }

    // ──────────────────────────────────────────────────────────────────────
    // Modify flow — add hats to a user already assigned to the post.
    // Hats offered are filtered to only those that exist on this post
    // (position hats) or are org-wide special-rights hats — never hats from
    // other posts. The service uses arrayUnion so the call is additive and
    // idempotent; approval on/off is handled by the same service method.
    // ──────────────────────────────────────────────────────────────────────

    /** Hats available for a modify picker: row position hats the user doesn't yet have. */
    public getAvailablePositionHatsForUser(row: TreeRow, user: AssignableUser): OrgHat[] {
        const worn = new Set(user.hatKeys || []);
        return row.hats.filter((h) => !worn.has(h.key));
    }

    /** Special-rights hats the user doesn't yet carry. */
    public getAvailableSpecialRightsHatsForUser(row: TreeRow, user: AssignableUser): OrgHat[] {
        const worn = new Set(user.hatKeys || []);
        return row.specialRightsHats.filter((h) => !worn.has(h.key));
    }

    /** Are there any hats left to offer for this user? */
    public hasHatsAvailableToAdd(row: TreeRow, user: AssignableUser): boolean {
        return (
            this.getAvailablePositionHatsForUser(row, user).length > 0 ||
            this.getAvailableSpecialRightsHatsForUser(row, user).length > 0
        );
    }

    public isUserBeingModified(row: TreeRow, user: AssignableUser): boolean {
        return row.usersToModify.some((u) => u.key === user.key);
    }

    public onToggleAddHats(row: TreeRow, user: AssignableUser): void {
        user.showHatPicker = !user.showHatPicker;
        if (user.showHatPicker && !user.hatKeysToAdd) {
            user.hatKeysToAdd = [];
        }
    }

    public onModifyHatSelectionChange(row: TreeRow, user: AssignableUser, selectedKeys: string[]): void {
        user.hatKeysToAdd = selectedKeys;
        const idx = row.usersToModify.findIndex((u) => u.key === user.key);
        if (selectedKeys.length > 0) {
            if (idx >= 0) {
                row.usersToModify[idx] = user;
            } else {
                row.usersToModify.push(user);
            }
        } else if (idx >= 0) {
            // No hats selected — back out of the modification queue
            row.usersToModify.splice(idx, 1);
        }
    }

    public onCancelModify(row: TreeRow, user: AssignableUser): void {
        user.hatKeysToAdd = [];
        user.showHatPicker = false;
        const idx = row.usersToModify.findIndex((u) => u.key === user.key);
        if (idx >= 0) row.usersToModify.splice(idx, 1);
    }

    public getModifyHatSummary(row: TreeRow, user: AssignableUser): string {
        return (user.hatKeysToAdd || []).map((k) => this.getHatName(row, k)).join(', ');
    }

    public getHatName(row: TreeRow, hatKey: string): string {
        const hat = row.hats.find((h) => h.key === hatKey) || row.specialRightsHats.find((h) => h.key === hatKey);
        return hat?.name || hatKey;
    }

    public isSpecialRightsHat(row: TreeRow, hatKey: string): boolean {
        return row.specialRightsHats.some((h) => h.key === hatKey);
    }

    public hasPositionHatSelected(row: TreeRow, user: AssignableUser): boolean {
        if (!user.selectedHatKeys?.length) return false;
        const positionHatKeys = new Set(row.hats.map((h) => h.key));
        return user.selectedHatKeys.some((k) => positionHatKeys.has(k));
    }

    /** All added users must have at least one position hat selected */
    public get allHatsValid(): boolean {
        for (const row of this.rows) {
            for (const user of row.usersToAdd) {
                if (!this.hasPositionHatSelected(row, user)) return false;
            }
        }
        return true;
    }

    public isUserBeingRemoved(row: TreeRow, user: AssignableUser): boolean {
        return row.usersToRemove.some((u) => u.key === user.key);
    }

    public get hasChanges(): boolean {
        return this.rows.some(
            (r) =>
                r.usersToAdd.length > 0 ||
                r.usersToRemove.length > 0 ||
                r.usersToApprove.length > 0 ||
                r.usersToReject.length > 0 ||
                r.usersToModify.length > 0
        );
    }

    public get changesSummary(): {
        action: 'add' | 'remove' | 'approve' | 'reject' | 'modify';
        userName: string;
        postName: string;
        pending: boolean;
        hatNames?: string;
    }[] {
        const changes: {
            action: 'add' | 'remove' | 'approve' | 'reject' | 'modify';
            userName: string;
            postName: string;
            pending: boolean;
            hatNames?: string;
        }[] = [];
        for (const row of this.rows) {
            for (const user of row.usersToApprove) {
                changes.push({ action: 'approve', userName: user.name, postName: row.name, pending: false });
            }
            for (const user of row.usersToReject) {
                changes.push({ action: 'reject', userName: user.name, postName: row.name, pending: false });
            }
            for (const user of row.usersToAdd) {
                const hatNames = (user.selectedHatKeys || []).map((k) => this.getHatName(row, k)).join(', ');
                changes.push({
                    action: 'add',
                    userName: user.name,
                    postName: row.name,
                    pending: this.requireApproval,
                    hatNames,
                });
            }
            for (const user of row.usersToModify) {
                const hatNames = (user.hatKeysToAdd || []).map((k) => this.getHatName(row, k)).join(', ');
                changes.push({
                    action: 'modify',
                    userName: user.name,
                    postName: row.name,
                    pending: this.requireApproval,
                    hatNames,
                });
            }
            for (const user of row.usersToRemove) {
                changes.push({ action: 'remove', userName: user.name, postName: row.name, pending: false });
            }
        }
        return changes;
    }

    public get postCount(): number {
        return this.rows.filter((r) => r.isPost).length;
    }

    public getPartTypeIcon(partType: string): string {
        switch (partType) {
            case 'section':
                return 'account_tree';
            case 'unit':
                return 'workspaces';
            case 'post':
                return 'person_pin';
            default:
                return 'folder';
        }
    }

    public async onSave(): Promise<void> {
        if (!this.hasChanges || this.isSaving) return;

        this.isSaving = true;
        this.savePhase = 'writing';
        const changes = this.changesSummary;
        this.totalChanges = changes.length;
        this.saveProgress = 0;
        this.saveStartTime = Date.now();

        const orgId = this.data.organization.key;
        const errors: string[] = [];
        let successCount = 0;
        let pendingCount = 0;
        // Track whether any operations will trigger backend security tasks.
        // Pending-approval additions and rejections do NOT trigger hat writes.
        let expectsBackendTasks = false;
        this.saveResults = [];

        try {
            for (const row of this.rows) {
                // Process approvals — moves to assignedToArray → triggers hat write
                for (const user of row.usersToApprove) {
                    expectsBackendTasks = true;
                    if (user.approvalId) {
                        const hatKeys = row.hats.map((h) => h.key);
                        const result = await this.orgHattingService.approvePositionAssignment(
                            orgId,
                            user.approvalId,
                            hatKeys
                        );
                        if (!result.succeeded) {
                            errors.push(`Failed to approve ${user.name} on ${row.name}: ${result.error}`);
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'failed',
                                icon: 'error',
                                iconClass: 'failed',
                            });
                        } else {
                            successCount++;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'approved',
                                icon: 'check_circle',
                                iconClass: 'approved',
                            });
                        }
                    } else {
                        const result = await this.orgHattingService.approvePendingUserDirectly(
                            orgId,
                            row.hats,
                            user.key
                        );
                        if (!result.succeeded) {
                            errors.push(`Failed to approve ${user.name} on ${row.name}: ${result.error}`);
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'failed',
                                icon: 'error',
                                iconClass: 'failed',
                            });
                        } else {
                            successCount++;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'approved',
                                icon: 'check_circle',
                                iconClass: 'approved',
                            });
                        }
                    }
                    this.saveProgress++;
                }

                // Process rejections — only touches pendingAssignedToArray, no hat write trigger
                for (const user of row.usersToReject) {
                    if (user.approvalId) {
                        const result = await this.orgHattingService.rejectPositionAssignment(orgId, user.approvalId);
                        if (!result.succeeded) {
                            errors.push(`Failed to reject ${user.name} from ${row.name}: ${result.error}`);
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'failed',
                                icon: 'error',
                                iconClass: 'failed',
                            });
                        } else {
                            successCount++;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'rejected',
                                icon: 'cancel',
                                iconClass: 'rejected',
                            });
                        }
                    } else {
                        await this.orgHattingService.removePendingUserFromHats(orgId, row.hats, user.key);
                        successCount++;
                        this.saveResults.push({
                            userName: user.name,
                            postName: row.name,
                            outcome: 'rejected',
                            icon: 'cancel',
                            iconClass: 'rejected',
                        });
                    }
                    this.saveProgress++;
                }

                // Process removals (active users) — removes from assignedToArray → triggers hat write
                for (const user of row.usersToRemove) {
                    expectsBackendTasks = true;
                    let removeFailed = false;
                    for (const hat of row.hats) {
                        if (hat.assignedToArray?.includes(user.key)) {
                            const result = await this.orgHattingService.removeUserFromHat(orgId, hat.key, user.key);
                            if (!result.succeeded) {
                                errors.push(`Failed to remove ${user.name} from ${row.name}: ${result.error}`);
                                removeFailed = true;
                            }
                        }
                    }
                    if (removeFailed) {
                        this.saveResults.push({
                            userName: user.name,
                            postName: row.name,
                            outcome: 'failed',
                            icon: 'error',
                            iconClass: 'failed',
                        });
                    } else {
                        successCount++;
                        this.saveResults.push({
                            userName: user.name,
                            postName: row.name,
                            outcome: 'removed',
                            icon: 'person_remove',
                            iconClass: 'removed',
                        });
                    }
                    this.saveProgress++;
                }

                // Process additions (new users)
                for (const user of row.usersToAdd) {
                    if (row.hats.length === 0) {
                        errors.push(`Cannot assign ${user.name} to ${row.name}: No hats on this post`);
                        this.saveResults.push({
                            userName: user.name,
                            postName: row.name,
                            outcome: 'failed',
                            icon: 'error',
                            iconClass: 'failed',
                        });
                        this.saveProgress++;
                        continue;
                    }

                    const allSelectedKeys = user.selectedHatKeys || row.hats.map((h) => h.key);
                    const positionHatKeys = new Set(row.hats.map((h) => h.key));
                    const hatKeys = allSelectedKeys.filter((k) => positionHatKeys.has(k));
                    const specialRightsHatKeys = allSelectedKeys.filter((k) => !positionHatKeys.has(k));

                    const result = await this.orgHattingService.assignUserToPosition(
                        orgId,
                        row.partKey,
                        row.name,
                        hatKeys,
                        user.key,
                        this.requireApproval,
                        specialRightsHatKeys.length > 0 ? specialRightsHatKeys : undefined
                    );
                    if (!result.succeeded) {
                        errors.push(`Failed to assign ${user.name} to ${row.name}: ${result.error}`);
                        this.saveResults.push({
                            userName: user.name,
                            postName: row.name,
                            outcome: 'failed',
                            icon: 'error',
                            iconClass: 'failed',
                        });
                    } else {
                        successCount++;
                        if (result.pending) {
                            pendingCount++;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'awaiting approval',
                                icon: 'schedule',
                                iconClass: 'pending',
                            });
                        } else {
                            // Direct assignment (no approval) writes to assignedToArray → triggers hat write
                            expectsBackendTasks = true;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'posted',
                                icon: 'person_add',
                                iconClass: 'posted',
                            });
                        }
                    }
                    this.saveProgress++;
                }

                // Process modifications (existing users gaining extra hats).
                // Two paths:
                //   a) ACTIVE user — call assignUserToPosition, which handles
                //      requireApproval on/off correctly using arrayUnion
                //      everywhere. Existing hat assignments are preserved.
                //   b) PENDING user — user is already in pendingAssignedToArray
                //      awaiting approval. Instead of creating a second approval
                //      record, we merge the new hats into the existing approval
                //      (via addHatsToPendingAssignment) so the approver sees
                //      the full set in one go. Ignores the current
                //      requireApproval setting — if the user is pending, new
                //      hats go to the pending side.
                for (const user of row.usersToModify) {
                    const selected = user.hatKeysToAdd || [];
                    if (selected.length === 0) {
                        // Nothing to do — shouldn't happen but guard anyway
                        this.saveProgress++;
                        continue;
                    }
                    const positionHatKeys = new Set(row.hats.map((h) => h.key));
                    const hatKeys = selected.filter((k) => positionHatKeys.has(k));
                    const specialRightsHatKeys = selected.filter((k) => !positionHatKeys.has(k));

                    let result: { succeeded: boolean; pending: boolean; error?: string };
                    if (user.isPending) {
                        result = await this.orgHattingService.addHatsToPendingAssignment(
                            orgId,
                            user.approvalId,
                            user.key,
                            hatKeys,
                            specialRightsHatKeys
                        );
                    } else {
                        result = await this.orgHattingService.assignUserToPosition(
                            orgId,
                            row.partKey,
                            row.name,
                            hatKeys,
                            user.key,
                            this.requireApproval,
                            specialRightsHatKeys.length > 0 ? specialRightsHatKeys : undefined
                        );
                    }
                    if (!result.succeeded) {
                        errors.push(`Failed to add hats for ${user.name} on ${row.name}: ${result.error}`);
                        this.saveResults.push({
                            userName: user.name,
                            postName: row.name,
                            outcome: 'failed',
                            icon: 'error',
                            iconClass: 'failed',
                        });
                    } else {
                        successCount++;
                        if (result.pending) {
                            pendingCount++;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'awaiting approval',
                                icon: 'schedule',
                                iconClass: 'pending',
                            });
                        } else {
                            expectsBackendTasks = true;
                            this.saveResults.push({
                                userName: user.name,
                                postName: row.name,
                                outcome: 'posted',
                                icon: 'person_add',
                                iconClass: 'posted',
                            });
                        }
                    }
                    this.saveProgress++;
                }
            }

            if (errors.length > 0) {
                console.error('Bulk assign errors:', errors);
            }

            if (expectsBackendTasks) {
                // Phase 2: Monitor backend task processing
                this.savePhase = 'processing';
                this.monitorBackendTasks();
            } else {
                // No backend tasks expected (all operations were pending-approval or rejections)
                // Go straight to done
                this.finishSave();
            }
        } catch (error) {
            console.error('Failed to save changes:', error);
            this.savePhase = 'error';
            this.isSaving = false;
        }
    }

    private monitorBackendTasks(): void {
        const orgParts = this.data.organization?.orgParts || [];
        this.taskStatuses = [];
        this.tasksCompleted = 0;
        this.totalTasks = 0;

        const timeoutHandle = setTimeout(() => {
            console.warn('[AssignStaff] Task monitor timed out after 60s');
            this.finishSave();
        }, 60000);

        this.taskMonitorSub = this.obService.monitorSecurityTasks().subscribe((tasks) => {
            const recentTasks = tasks.filter((t) => {
                const created = t.createdTimeStamp?.toMillis?.() || (t.createdTimeStamp as any)?.seconds * 1000 || 0;
                return created > this.saveStartTime - 2000;
            });

            let completed = 0;
            let total = 0;
            const statuses: typeof this.taskStatuses = [];

            for (const task of recentTasks) {
                const isTerminal =
                    task.status === 'completed' ||
                    task.status === 'fulfilled' ||
                    task.status === 'error' ||
                    task.status === 'Error' ||
                    task.status === 'failed';
                const isSuccess = task.status === 'completed' || task.status === 'fulfilled';

                let statusIcon = 'hourglass_empty';
                let statusLabel = 'queued';
                if (task.status === 'processing') {
                    statusIcon = 'sync';
                    statusLabel = 'processing';
                } else if (isSuccess) {
                    statusIcon = 'check_circle';
                    statusLabel = 'completed';
                } else if (isTerminal) {
                    statusIcon = 'error';
                    statusLabel = task.error || 'failed';
                }

                let icon = 'settings';
                let actionLabel: string = task.typeOfTask || 'processing';
                if (task.typeOfTask === 'addHat') {
                    icon = 'person_add';
                    actionLabel = 'add hat';
                } else if (task.typeOfTask === 'updateHat') {
                    icon = 'person';
                    actionLabel = 'update hat';
                } else if (task.typeOfTask === 'removeHat') {
                    icon = 'person_remove';
                    actionLabel = 'remove hat';
                } else if (task.typeOfTask === 'updatePart') {
                    icon = 'edit';
                    actionLabel = 'update';
                } else if (task.typeOfTask === 'addPart') {
                    icon = 'add_circle';
                    actionLabel = 'add';
                }

                let name = 'Unknown';
                if (task.partKey) {
                    const part = orgParts.find((p) => p.partKey === task.partKey);
                    name = part?.name || task.snapshotNew?.name || task.diffNew?.name || task.partKey;
                }
                if (name === 'Unknown' && task.hatId) {
                    const orgHats = (this.data.organization as any)?.orgHats;
                    if (orgHats) {
                        const hat = orgHats[task.hatId];
                        name = hat?.name || hat?.positionName || task.snapshotNew?.name || task.hatId;
                    } else {
                        name = task.snapshotNew?.name || task.hatId;
                    }
                }

                statuses.push({ key: task.key, name, action: actionLabel, icon, status: statusLabel, statusIcon });
                total++;
                if (isTerminal) completed++;
            }

            this.taskStatuses = statuses;
            this.totalTasks = total;
            this.tasksCompleted = completed;

            if (total > 0 && completed === total) {
                clearTimeout(timeoutHandle);
                setTimeout(() => this.finishSave(), 1500);
            }
        });
    }

    private finishSave(): void {
        this.taskMonitorSub?.unsubscribe();
        this.savePhase = 'done';
        this.isSaving = false;
    }

    continueEditing(): void {
        const freshOrg = this.obService.organization;
        if (freshOrg) {
            this.data.organization = freshOrg;
        }

        this.requireApproval = !!this.data.organization.settings?.requireHatAssignmentApproval;
        this.buildUserList();
        this.buildTree();
        this.loadPendingApprovals();

        this.savePhase = 'idle';
        this.isSaving = false;
        this.saveProgress = 0;
        this.totalChanges = 0;
        this.taskStatuses = [];
        this.saveResults = [];
        this.tasksCompleted = 0;
        this.totalTasks = 0;
    }
}
